import {
  Accordion,
  AccordionDetails,
  AccordionIcon,
  AccordionSummary,
  AccordionTitle,
  Description,
  Menu,
  Modal,
  PageTitle,
  SandboxSvg,
  Tooltip,
  WidgetList,
} from "pixel";
import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { RenderByUserRole } from "../packages/accessControl/ComponentRBAC";
import { useFpapi } from "../packages/context/fpapi/useFpapi";
import { Role } from "../packages/context/userinfo/types";
import { useUserInfo } from "../packages/context/userinfo/useUserInfo";
import { useGlobalAppStore } from "../packages/context/store/useGlobalAppStore";
import { reportErrorToSentry } from "../packages/global/sentry/sentry";
import {
  MANUAL_BANK_ACCOUNT_VERIFICATION_ENABLED_TENANTS,
  OLD_DASHBOARD_LINK,
  ONDC_TRANSACTIONS_ENABLED_TENANTS,
  PURCHASE_OVERVIEW_ENABLED_TENANTS,
  THIRDEYE_NAVIGATION_URL,
  THIRDEYE_WIAM_CLIENT_ID,
  VALID_THIRDEYE_ROLES,
  VALID_MEMBERSHIP_PORTAL_ROLES,
} from "../packages/utils/constants";
import { ConditionalRenderer } from "../packages/utils/helpers";
import {
  ProtectedExternalLink,
  ProtectedExternalLinkButton,
  ProtectedMenu,
  ProtectedMenuItem,
} from "../packages/utils/routeHelpers";
import { getLast30MinsDateRangeStr, tenantEnv } from "../packages/utils/utils";
import FpConsoleNavItems from "./FpConsoleNavItems";
import { BaseRoutes } from "../packages/utils/routes";
import OrdersTrendsNavLinkConditionalRenderer from "../packages/components/functional/Orders/OrderCharts/OrdersTrendsNavLinkConditionalRenderer";
import RenderByProbeServiceConfig from "../packages/accessControl/RenderByProbeServiceConfig";
import appConfig from "../packages/config/appConfig";
import { isInvestorsV1Accessible } from "../packages/components/functional/Investors/utils";
import { isKycFormsPagesAccessible } from "../packages/components/functional/Investors/KycForms/utils";
import { isKycStatusChecksAccessible } from "../packages/components/functional/Verifications/KycStatusChecks/utils";
import { matchCurrentPath } from "./utils";

type NavGroupItem =
  | "OPERATIONS"
  | "CONFIGURATIONS"
  | "DEVELOPERS"
  | "FPACCOUNT"
  | "PARTNERS"
  | "ORDERS"
  | "FEEDS"
  | "PAYMENTS"
  | "PAYOUTS"
  | "INVESTORS"
  | "KYC"
  | "METRICS"
  | "SUPPORT"
  | "VERIFICATIONS";

interface NavItemsProps {
  handleSideBar: (param: boolean) => void;
  isSideBarCollapsed: boolean;
}

const NavItems = ({ handleSideBar, isSideBarCollapsed }: NavItemsProps) => {
  const { fpapi } = useFpapi();
  const userInfo = useUserInfo();
  const { isAmcTenant, supportedGateways } = useGlobalAppStore();
  const canAccessInvestorsV1 = isInvestorsV1Accessible(supportedGateways, isAmcTenant);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const activeTenant = userInfo?.data.activeTenant;
  const canAccessKycForms = isKycFormsPagesAccessible(activeTenant);
  const canAccessKycStatusChecks = isKycStatusChecksAccessible(activeTenant, isAmcTenant);

  const location = useLocation();
  const opsPortalRedirectUrl = `${OLD_DASHBOARD_LINK}login?realm=${
    userInfo?.data.userInfo?.type === "internal" ? "root" : activeTenant
  }&tenant=${userInfo?.data.activeTenant}`;

  const hasValidRolesToAccessThirdeye = VALID_THIRDEYE_ROLES.some((role) =>
    userInfo?.data?.userInfo?.roles?.includes(role as Role)
  );
  const hasValidRolesToAccessMembershipPortal = VALID_MEMBERSHIP_PORTAL_ROLES.some((role) =>
    userInfo?.data?.userInfo?.roles?.includes(role as Role)
  );
  const showExternalApplications =
    appConfig.app.is_old_dashboard_link_enabled ||
    (appConfig.app.is_thirdeye_link_enabled && hasValidRolesToAccessThirdeye) ||
    (appConfig.app.is_membership_console_link_enabled && hasValidRolesToAccessMembershipPortal);
  const thirdeyeRedirectURLObject = new URL(
    THIRDEYE_NAVIGATION_URL.replace("{{realm}}", userInfo?.data.activeTenant ?? "")
  );
  if (userInfo?.data.userInfo?.type === "internal") {
    thirdeyeRedirectURLObject.searchParams.append("internal", "true");
  }
  const thirdeyeRedirectUrl = thirdeyeRedirectURLObject.toString();

  const membershipPortalRedirectUrl = useMemo(() => {
    if (!appConfig?.app?.membership_portal_url) {
      return "";
    }

    const url = new URL(appConfig.app.membership_portal_url);

    if (userInfo?.data?.userInfo?.type === "internal") {
      url.searchParams.append("user_type", "internal");
    }

    return url.toString();
  }, [userInfo?.data?.userInfo?.type]);

  const isPathActive = (requiredRoute: string) => matchCurrentPath(location, requiredRoute);

  const [expanded, setExpanded] = useState<NavGroupItem | false>(() => {
    const orderPaths = [
      BaseRoutes.ORDERS,
      BaseRoutes.MF_PURCHASES,
      BaseRoutes.MF_PURCHASE_PLANS,
      BaseRoutes.MF_REDEMPTIONS,
      BaseRoutes.MF_REDEMPTION_PLANS,
      BaseRoutes.MF_SWITCHES,
      BaseRoutes.MF_SWITCH_PLANS,
    ];

    const feedPaths = [
      BaseRoutes.FEEDS,
      BaseRoutes.FEED_SUBMISSION_JOBS,
      BaseRoutes.FEED_SUBMISSIONS,
    ];

    const paymentPaths = [BaseRoutes.PAYMENTS, BaseRoutes.MANDATES];

    const payoutPaths = [
      BaseRoutes.PAYOUTS,
      BaseRoutes.ORDERFEED,
      BaseRoutes.RECON,
      BaseRoutes.DEPOSIT_RECEIPTS,
      BaseRoutes.DEPOSIT_RECEIPT_ADJUSTMENTS,
      BaseRoutes.RECON_ADJUSTMENTS,
      BaseRoutes.SETTLEMENT_INSTRUCTIONS,
      BaseRoutes.SETTLEMENT_REPORT_FILES,
      BaseRoutes.BANK_ACCOUNT_STATEMENTS,
    ];

    const investorPaths = [
      BaseRoutes.INVESTORS,
      BaseRoutes.INVESTOR_PROFILES,
      BaseRoutes.MF_INVESTMENT_ACCOUNTS,
      BaseRoutes.INVESTOR_PROFILE_NON_INDIVIDUALS,
      BaseRoutes.KYC_REQUESTS,
      BaseRoutes.KYC_FORMS,
      BaseRoutes.MF_FOLIOS,
      BaseRoutes.BANK_ACCOUNT_VERIFICATIONS,
    ];

    const developerPaths = [BaseRoutes.DEVELOPERS, BaseRoutes.EVENTS];

    const fpAccountPaths = [
      BaseRoutes.FP_ACCOUNT,
      BaseRoutes.SCHEDULED_QUERIES,
      BaseRoutes.NOTIFICATION_WEBHOOKS,
    ];

    const operationPaths = [BaseRoutes.OPERATIONS, BaseRoutes.TRANSACTIONS];

    const verificationPaths = [
      BaseRoutes.PAN_VERIFICATIONS,
      BaseRoutes.KYC,
      BaseRoutes.BANK_ACCOUNT_VERIFICATION,
    ];

    const currentPathToGroupMapping = {
      [BaseRoutes.CONFIGURATIONS]: "CONFIGURATIONS",
      [BaseRoutes.PARTNERS]: "PARTNERS",
      [BaseRoutes.NETWORK_INTELLIGENCE]: "PARTNERS",
      [BaseRoutes.METRICS]: "METRICS",
      [BaseRoutes.SUPPORT]: "SUPPORT",
      ...Object.fromEntries(verificationPaths.map((path) => [path, "VERIFICATIONS"])),
      ...Object.fromEntries(operationPaths.map((path) => [path, "OPERATIONS"])),
      ...Object.fromEntries(orderPaths.map((path) => [path, "ORDERS"])),
      ...Object.fromEntries(feedPaths.map((path) => [path, "FEEDS"])),
      ...Object.fromEntries(paymentPaths.map((path) => [path, "PAYMENTS"])),
      ...Object.fromEntries(payoutPaths.map((path) => [path, "PAYOUTS"])),
      ...Object.fromEntries(investorPaths.map((path) => [path, "INVESTORS"])),
      ...Object.fromEntries(developerPaths.map((path) => [path, "DEVELOPERS"])),
      ...Object.fromEntries(fpAccountPaths.map((path) => [path, "FPACCOUNT"])),
    } as const;

    let navGroupItem: NavGroupItem | false = false;

    let key: keyof typeof currentPathToGroupMapping;
    for (key in currentPathToGroupMapping) {
      if (isPathActive(key)) {
        navGroupItem = currentPathToGroupMapping[key] as NavGroupItem;
      }
    }
    return navGroupItem;
  });

  // Temp hack to hide operations menu for test users. To be removed.
  const isOnlyTestUser =
    userInfo?.data?.userInfo?.roles?.length === 1 &&
    userInfo?.data?.userInfo?.roles?.includes(Role.TEST);

  const handleAccordionChange = (panel: NavGroupItem) => {
    return (e: React.MouseEvent<HTMLDivElement, MouseEvent>, isExpanded: boolean) => {
      const shouldExpand = isExpanded || isSideBarCollapsed;
      setExpanded(shouldExpand ? panel : false);
      handleSideBar(false);
    };
  };

  const isFpConsoleActive = isPathActive("/fp-console");

  const apiLogsUrlSearchParams = new URLSearchParams({
    fetch_count: "20",
    status: "500,502",
    date_range: getLast30MinsDateRangeStr(),
  }).toString();

  const handleShouldOpenThirdeyeLink = async () => {
    try {
      setIsModalOpen(false);

      const [tenantconfigurationdata, wiamClients] = await Promise.all([
        fpapi?.fpDashboardClient?.data_platform()?.fetchTenant(),
        fpapi?.fpDashboardClient?.access_management().listWiamClients(`fpa_${activeTenant}`),
      ]);

      const isDataPlatformConfigured = tenantconfigurationdata?.is_onboarded;
      const isThirdeyeClientConfigured = wiamClients?.data?.findIndex(
        (wiamClient) => wiamClient.clientId === THIRDEYE_WIAM_CLIENT_ID
      );

      if (isDataPlatformConfigured && isThirdeyeClientConfigured) {
        return true;
      } else {
        setIsModalOpen(true);
        return false;
      }
    } catch (e) {
      reportErrorToSentry(e);
      setIsModalOpen(true);
      return false;
    }
  };

  return isFpConsoleActive ? (
    <FpConsoleNavItems isSideBarCollapsed={isSideBarCollapsed} />
  ) : (
    <>
      <ConditionalRenderer check={!isOnlyTestUser}>
        <ProtectedMenu matchRoute={BaseRoutes.OPERATIONS}>
          <div>
            <Accordion
              expanded={expanded === "OPERATIONS"}
              onChange={handleAccordionChange("OPERATIONS")}
            >
              <AccordionSummary active={expanded === "OPERATIONS"}>
                <Tooltip
                  placement="right"
                  content="Operations"
                  hidden={!isSideBarCollapsed}
                  customClass="block"
                >
                  <AccordionIcon>Operation</AccordionIcon>
                </Tooltip>
                <AccordionTitle>Operations</AccordionTitle>
              </AccordionSummary>

              <div className="collapse-menu-title">Operations</div>
              <AccordionDetails>
                <WidgetList>
                  <ProtectedMenuItem
                    redirectTo={`${BaseRoutes.OPERATIONS}/order-operations`}
                    label="Order operations"
                  />
                  <ProtectedMenuItem
                    redirectTo={`${BaseRoutes.OPERATIONS}/feed-file`}
                    label="Request feed file"
                  />
                  <ConditionalRenderer
                    check={ONDC_TRANSACTIONS_ENABLED_TENANTS.includes(activeTenant ?? "")}
                  >
                    <ProtectedMenuItem
                      redirectTo={`${BaseRoutes.OPERATIONS}/ondc-transactions`}
                      label="ONDC Transactions"
                    />
                  </ConditionalRenderer>
                  <ConditionalRenderer check={appConfig.app.is_aging_orders_enabled}>
                    <ProtectedMenuItem
                      redirectTo={`${BaseRoutes.OPERATIONS}/aging-orders`}
                      label="Aging orders"
                    />
                  </ConditionalRenderer>
                  <ProtectedMenuItem redirectTo={BaseRoutes.TRANSACTIONS} label="Transactions" />
                  <ProtectedMenuItem
                    redirectTo={`${BaseRoutes.OPERATIONS}/transfers`}
                    label="Payment transfers"
                  />
                  <ProtectedMenuItem
                    redirectTo={`${BaseRoutes.OPERATIONS}/fund-schemes`}
                    label="Fund schemes"
                  />
                  <ProtectedMenuItem
                    redirectTo={`${BaseRoutes.OPERATIONS}/blocked-bank-accounts`}
                    label="Blocked bank accounts"
                  />
                  <ConditionalRenderer
                    check={MANUAL_BANK_ACCOUNT_VERIFICATION_ENABLED_TENANTS.includes(
                      activeTenant ?? ""
                    )}
                  >
                    <ProtectedMenuItem
                      redirectTo={`${BaseRoutes.OPERATIONS}/manual-bank-account-verification`}
                      label="Manual bank account verifications"
                    />
                  </ConditionalRenderer>
                </WidgetList>
              </AccordionDetails>
            </Accordion>
          </div>
        </ProtectedMenu>
      </ConditionalRenderer>

      <ProtectedMenu matchRoute={BaseRoutes.ORDERS}>
        <div>
          <Accordion expanded={expanded === "ORDERS"} onChange={handleAccordionChange("ORDERS")}>
            <AccordionSummary active={expanded === "ORDERS"}>
              <Tooltip
                placement="right"
                content="Orders"
                hidden={!isSideBarCollapsed}
                customClass="block"
              >
                <AccordionIcon>Order</AccordionIcon>
              </Tooltip>
              <AccordionTitle> Orders</AccordionTitle>
            </AccordionSummary>

            <div className="collapse-menu-title">Orders</div>

            <AccordionDetails>
              <WidgetList>
                <ConditionalRenderer
                  check={
                    tenantEnv.isStagingOrSandbox &&
                    PURCHASE_OVERVIEW_ENABLED_TENANTS.includes(activeTenant ?? "")
                  }
                >
                  <ProtectedMenuItem
                    redirectTo={`${BaseRoutes.ORDERS}/overview`}
                    label="Purchases overview"
                  />
                </ConditionalRenderer>
                <ProtectedMenuItem redirectTo={BaseRoutes.MF_PURCHASES} label="Purchases" />
                <ProtectedMenuItem
                  redirectTo={BaseRoutes.MF_PURCHASE_PLANS}
                  label="Purchase plans"
                />
                <ProtectedMenuItem redirectTo={BaseRoutes.MF_REDEMPTIONS} label="Redemptions" />
                <ProtectedMenuItem
                  redirectTo={BaseRoutes.MF_REDEMPTION_PLANS}
                  label="Redemption plans"
                />
                <ProtectedMenuItem redirectTo={BaseRoutes.MF_SWITCHES} label="Switches" />
                <ProtectedMenuItem redirectTo={BaseRoutes.MF_SWITCH_PLANS} label="Switch plans" />

                <OrdersTrendsNavLinkConditionalRenderer>
                  <RenderByProbeServiceConfig requiredProbeServices={["oms"]}>
                    <ProtectedMenuItem
                      redirectTo={`${BaseRoutes.ORDERS}/orders-trend`}
                      label="Orders trend"
                    />
                  </RenderByProbeServiceConfig>
                </OrdersTrendsNavLinkConditionalRenderer>
              </WidgetList>
            </AccordionDetails>
          </Accordion>
        </div>
      </ProtectedMenu>

      <ConditionalRenderer check={appConfig.app.are_feed_submission_pages_allowed}>
        <ProtectedMenu matchRoute={BaseRoutes.FEEDS}>
          <div>
            <Accordion expanded={expanded === "FEEDS"} onChange={handleAccordionChange("FEEDS")}>
              <AccordionSummary active={expanded === "FEEDS"}>
                <Tooltip
                  placement="right"
                  content="Feeds"
                  hidden={!isSideBarCollapsed}
                  customClass="block"
                >
                  <AccordionIcon>Transaction</AccordionIcon>
                </Tooltip>
                <AccordionTitle>Feeds</AccordionTitle>
              </AccordionSummary>

              <div className="collapse-menu-title">Feeds</div>

              <AccordionDetails>
                <WidgetList>
                  <ProtectedMenuItem
                    redirectTo={BaseRoutes.FEED_SUBMISSIONS}
                    label="Feed submissions"
                  />

                  <ProtectedMenuItem
                    redirectTo={BaseRoutes.FEED_SUBMISSION_JOBS}
                    label="Feed submission jobs"
                  />
                </WidgetList>
              </AccordionDetails>
            </Accordion>
          </div>
        </ProtectedMenu>
      </ConditionalRenderer>

      <ProtectedMenu matchRoute={BaseRoutes.METRICS}>
        <div>
          <RenderByProbeServiceConfig
            requiredProbeServices={[
              "oms",
              "bank_account_verification",
              "kyc_verification",
              "warehouse",
            ]}
          >
            <Accordion
              expanded={expanded === "METRICS"}
              onChange={handleAccordionChange("METRICS")}
            >
              <AccordionSummary active={expanded === "METRICS"}>
                <Tooltip
                  placement="right"
                  content="Metrics"
                  hidden={!isSideBarCollapsed}
                  customClass="block"
                >
                  <AccordionIcon>Metrics</AccordionIcon>
                </Tooltip>
                <AccordionTitle> Performance overview</AccordionTitle>
              </AccordionSummary>

              <div className="collapse-menu-title">Metrics</div>

              <AccordionDetails>
                <WidgetList>
                  <ProtectedMenuItem
                    redirectTo={`${BaseRoutes.METRICS}/product-usage`}
                    label="Product usage"
                  />
                </WidgetList>
              </AccordionDetails>
            </Accordion>
          </RenderByProbeServiceConfig>
        </div>
      </ProtectedMenu>

      <ProtectedMenu matchRoute={BaseRoutes.PAYMENTS}>
        <div>
          <Accordion
            expanded={expanded === "PAYMENTS"}
            onChange={handleAccordionChange("PAYMENTS")}
          >
            <AccordionSummary active={expanded === "PAYMENTS"}>
              <Tooltip
                placement="right"
                content="Payments"
                hidden={!isSideBarCollapsed}
                customClass="block"
              >
                <AccordionIcon>Bank</AccordionIcon>
              </Tooltip>
              <AccordionTitle>Payments</AccordionTitle>
            </AccordionSummary>

            <div className="collapse-menu-title">Payments</div>

            <AccordionDetails>
              <WidgetList>
                <ProtectedMenuItem redirectTo={BaseRoutes.PAYMENTS} label="Payments" />
                <ProtectedMenuItem redirectTo={BaseRoutes.MANDATES} label="Mandates" />
              </WidgetList>
            </AccordionDetails>
          </Accordion>
        </div>
      </ProtectedMenu>

      <ProtectedMenu matchRoute={BaseRoutes.PAYOUTS}>
        <div>
          <Accordion expanded={expanded === "PAYOUTS"} onChange={handleAccordionChange("PAYOUTS")}>
            <AccordionSummary active={expanded === "PAYOUTS"}>
              <Tooltip
                placement="right"
                content="Payouts"
                hidden={!isSideBarCollapsed}
                customClass="block"
              >
                <AccordionIcon>financeOutlined</AccordionIcon>
              </Tooltip>
              <AccordionTitle>Payouts</AccordionTitle>
            </AccordionSummary>

            <div className="collapse-menu-title">Payouts</div>

            <AccordionDetails>
              <WidgetList>
                <ProtectedMenuItem redirectTo={BaseRoutes.ORDERFEED} label="Order feed" />
                <ProtectedMenuItem
                  redirectTo={BaseRoutes.BANK_ACCOUNT_STATEMENTS}
                  label="Bank account statements"
                />
                <ProtectedMenuItem
                  redirectTo={`${BaseRoutes.DEPOSIT_RECEIPTS}?order_type=MF_PURCHASE`}
                  label="Deposit receipts"
                />
                <ProtectedMenuItem redirectTo={BaseRoutes.RECON} label="Reconciliations" />
                <ProtectedMenuItem
                  redirectTo={BaseRoutes.DEPOSIT_RECEIPT_ADJUSTMENTS}
                  label="Deposit receipt adjustments"
                />
                <ProtectedMenuItem
                  redirectTo={BaseRoutes.RECON_ADJUSTMENTS}
                  label="Reconciliations adjustments"
                />
                <ProtectedMenuItem
                  redirectTo={`${BaseRoutes.SETTLEMENT_INSTRUCTIONS}?order_type=MF_PURCHASE`}
                  label="Settlement instructions"
                />
                <ProtectedMenuItem
                  redirectTo={BaseRoutes.SETTLEMENT_REPORT_FILES}
                  label="RTA MIS Files"
                />
                <ProtectedMenuItem redirectTo={BaseRoutes.PAYOUTS} label="Payouts" />
              </WidgetList>
            </AccordionDetails>
          </Accordion>
        </div>
      </ProtectedMenu>

      <ProtectedMenu matchRoute={[BaseRoutes.INVESTORS, BaseRoutes.INVESTOR_PROFILES]}>
        <div>
          <Accordion
            expanded={expanded === "INVESTORS"}
            onChange={handleAccordionChange("INVESTORS")}
          >
            <AccordionSummary active={expanded === "INVESTORS"}>
              <Tooltip
                placement="right"
                content="Investors"
                hidden={!isSideBarCollapsed}
                customClass="block"
              >
                <AccordionIcon>ContactOutlined</AccordionIcon>{" "}
              </Tooltip>
              <AccordionTitle>Investors</AccordionTitle>
            </AccordionSummary>

            <div className="collapse-menu-title">INVESTORS</div>

            <AccordionDetails>
              <WidgetList>
                <ProtectedMenuItem
                  redirectTo={BaseRoutes.MF_INVESTMENT_ACCOUNTS}
                  label="Investment accounts"
                />

                <ConditionalRenderer check={canAccessInvestorsV1}>
                  <ProtectedMenuItem redirectTo={BaseRoutes.INVESTORS} label="Investors" />
                </ConditionalRenderer>

                <ProtectedMenuItem
                  redirectTo={BaseRoutes.INVESTOR_PROFILES}
                  label="Investor profiles"
                />

                <ProtectedMenuItem
                  redirectTo={BaseRoutes.INVESTOR_PROFILE_NON_INDIVIDUALS}
                  label="Non individual investors"
                />

                <ProtectedMenuItem redirectTo={BaseRoutes.KYC_REQUESTS} label="KYC requests" />

                <ConditionalRenderer check={canAccessKycForms}>
                  <ProtectedMenuItem redirectTo={BaseRoutes.KYC_FORMS} label="KYC forms" />
                </ConditionalRenderer>

                <ProtectedMenuItem
                  redirectTo={BaseRoutes.BANK_ACCOUNT_VERIFICATIONS}
                  label="Bank account verifications"
                />

                <ProtectedMenuItem redirectTo={BaseRoutes.MF_FOLIOS} label="Folios" />
              </WidgetList>
            </AccordionDetails>
          </Accordion>
        </div>
      </ProtectedMenu>

      {/* AMC tenants are not covered by the /verifications roles but still get the KYC status check page, so include its roles whenever that page is accessible to show this group. */}
      <ProtectedMenu
        matchRoute={
          canAccessKycStatusChecks
            ? [BaseRoutes.VERIFICATIONS, BaseRoutes.KYC]
            : BaseRoutes.VERIFICATIONS
        }
        matchExact
      >
        <div>
          <Accordion
            expanded={expanded === "VERIFICATIONS"}
            onChange={handleAccordionChange("VERIFICATIONS")}
          >
            <AccordionSummary active={expanded === "VERIFICATIONS"}>
              <Tooltip
                placement="right"
                content="Verifications"
                hidden={!isSideBarCollapsed}
                customClass="block"
              >
                <AccordionIcon>ContactOutlined</AccordionIcon>
              </Tooltip>
              <AccordionTitle>Verifications</AccordionTitle>
            </AccordionSummary>

            <div className="collapse-menu-title">Verifications</div>
            <AccordionDetails>
              <WidgetList>
                <ProtectedMenuItem
                  redirectTo={BaseRoutes.PAN_VERIFICATIONS}
                  label="PAN verification"
                />
                <ConditionalRenderer check={canAccessKycStatusChecks}>
                  <ProtectedMenuItem redirectTo={BaseRoutes.KYC} label="KYC status check" />
                </ConditionalRenderer>
                <ProtectedMenuItem
                  redirectTo={BaseRoutes.BANK_ACCOUNT_VERIFICATION}
                  label="Bank account verification"
                />
              </WidgetList>
            </AccordionDetails>
          </Accordion>
        </div>
      </ProtectedMenu>

      <ProtectedMenu matchRoute={[BaseRoutes.PARTNERS, BaseRoutes.NETWORK_INTELLIGENCE]}>
        <div>
          <Accordion
            expanded={expanded === "PARTNERS"}
            onChange={handleAccordionChange("PARTNERS")}
          >
            <AccordionSummary active={expanded === "PARTNERS"}>
              <Tooltip
                placement="right"
                content="Partners"
                hidden={!isSideBarCollapsed}
                customClass="block"
              >
                <AccordionIcon>UserFilled</AccordionIcon>
              </Tooltip>
              <AccordionTitle>Partners</AccordionTitle>
            </AccordionSummary>

            <div className="collapse-menu-title">Partners</div>
            <AccordionDetails>
              <WidgetList>
                <ProtectedMenuItem redirectTo={BaseRoutes.PARTNERS} label="Partner management" />
                <ConditionalRenderer check={appConfig.app.is_network_intelligence_enabled}>
                  <ProtectedMenuItem
                    redirectTo={BaseRoutes.NETWORK_INTELLIGENCE}
                    label="Network intelligence"
                  />
                </ConditionalRenderer>
              </WidgetList>
            </AccordionDetails>
          </Accordion>
        </div>
      </ProtectedMenu>

      <ProtectedMenu matchRoute={BaseRoutes.DEVELOPERS}>
        <div>
          <Accordion
            expanded={expanded === "DEVELOPERS"}
            onChange={handleAccordionChange("DEVELOPERS")}
          >
            <AccordionSummary active={expanded === "DEVELOPERS"}>
              <Tooltip
                placement="right"
                content="Developers"
                hidden={!isSideBarCollapsed}
                customClass="block"
              >
                <AccordionIcon>Developer</AccordionIcon>
              </Tooltip>
              <AccordionTitle>Developers</AccordionTitle>
            </AccordionSummary>

            <div className="collapse-menu-title">Developers</div>
            <AccordionDetails>
              <WidgetList>
                <ProtectedMenuItem
                  redirectTo={`${BaseRoutes.DEVELOPERS}/api-logs?${apiLogsUrlSearchParams}`}
                  label="API Logs"
                />
                <ConditionalRenderer check={appConfig.app.is_monitoring_alerts_enabled}>
                  <ProtectedMenuItem
                    redirectTo={BaseRoutes.MONITORING_ALERTS}
                    label="Monitoring alerts"
                  />
                </ConditionalRenderer>
                <ConditionalRenderer check={appConfig.app.is_events_enabled}>
                  <ProtectedMenuItem redirectTo={BaseRoutes.EVENTS} label="Events" />
                </ConditionalRenderer>
              </WidgetList>
            </AccordionDetails>
          </Accordion>
        </div>
      </ProtectedMenu>

      <ProtectedMenu matchRoute={BaseRoutes.FP_ACCOUNT}>
        <div>
          <Accordion
            expanded={expanded === "FPACCOUNT"}
            onChange={handleAccordionChange("FPACCOUNT")}
          >
            <AccordionSummary active={expanded === "FPACCOUNT"}>
              <Tooltip
                placement="right"
                content="FP account"
                hidden={!isSideBarCollapsed}
                customClass="block"
              >
                <AccordionIcon>UserProfile</AccordionIcon>
              </Tooltip>
              <AccordionTitle>FP account</AccordionTitle>
            </AccordionSummary>

            <div className="collapse-menu-title">FP account</div>
            <AccordionDetails>
              <WidgetList>
                <ProtectedMenuItem
                  redirectTo={`${BaseRoutes.FP_ACCOUNT}/configuration-status`}
                  label="Configuration status"
                />
                <ProtectedMenuItem
                  redirectTo={`${BaseRoutes.FP_ACCOUNT}/fp-assist`}
                  label="FP Assist"
                />
                <ProtectedMenuItem
                  redirectTo={`${BaseRoutes.FP_ACCOUNT}/mf_services`}
                  label="MF orders"
                />
                <ProtectedMenuItem
                  redirectTo={`${BaseRoutes.FP_ACCOUNT}/payments_gateway`}
                  label="Payments"
                />
                <ProtectedMenuItem redirectTo={`${BaseRoutes.FP_ACCOUNT}/amcs`} label="AMCs" />

                <ProtectedMenuItem
                  redirectTo={`${BaseRoutes.FP_ACCOUNT}/verifications`}
                  label="Verification services"
                />
                <ProtectedMenuItem
                  redirectTo={`${BaseRoutes.FP_ACCOUNT}/kyc-services`}
                  label="KYC services"
                />
                <ProtectedMenuItem
                  redirectTo={`${BaseRoutes.FP_ACCOUNT}/bi-tool`}
                  label="Business intelligence tool"
                />

                <ProtectedMenuItem
                  redirectTo={`${BaseRoutes.FP_ACCOUNT}/feed-configuration`}
                  label="Feed configuration"
                />

                <ProtectedMenuItem
                  redirectTo={`${BaseRoutes.FP_ACCOUNT}/user_management`}
                  label="User management"
                />
                <ProtectedMenuItem
                  redirectTo={`${BaseRoutes.SCHEDULED_QUERIES}`}
                  label="Scheduled queries"
                />
                <ProtectedMenuItem
                  redirectTo={`${BaseRoutes.NOTIFICATION_WEBHOOKS}`}
                  label="Notification webhooks"
                />
              </WidgetList>
            </AccordionDetails>
          </Accordion>
        </div>
      </ProtectedMenu>

      <ConditionalRenderer check={tenantEnv.isStagingOrSandbox}>
        <ProtectedMenu matchRoute={BaseRoutes.SUPPORT}>
          <div>
            <Accordion
              expanded={expanded === "SUPPORT"}
              onChange={handleAccordionChange("SUPPORT")}
            >
              <AccordionSummary active={expanded === "SUPPORT"}>
                <Tooltip
                  placement="right"
                  content="Support"
                  hidden={!isSideBarCollapsed}
                  customClass="block"
                >
                  <AccordionIcon>Support</AccordionIcon>
                </Tooltip>
                <AccordionTitle>Support</AccordionTitle>
              </AccordionSummary>
              <div className="collapse-menu-title">Support</div>

              <AccordionDetails>
                <WidgetList>
                  <ProtectedMenuItem redirectTo={`${BaseRoutes.SUPPORT}/issues`} label="Issues" />
                </WidgetList>
              </AccordionDetails>
            </Accordion>
          </div>
        </ProtectedMenu>
      </ConditionalRenderer>

      <div>
        <ConditionalRenderer check={Boolean(!isSideBarCollapsed)}>
          <ConditionalRenderer check={showExternalApplications}>
            <RenderByUserRole resource="NavItems.ProtectedExternalApplicationsTitle">
              <PageTitle variant="group" title="External Applications" />
            </RenderByUserRole>
          </ConditionalRenderer>
        </ConditionalRenderer>

        <Menu>
          <ConditionalRenderer check={appConfig.app.is_old_dashboard_link_enabled}>
            <ProtectedExternalLink
              label="Old dashboard (Deprecated soon)"
              link={opsPortalRedirectUrl}
              tooltipText="Old dashboard (Deprecated soon)"
            />
          </ConditionalRenderer>

          <ConditionalRenderer check={appConfig.app.is_thirdeye_link_enabled}>
            <ConditionalRenderer check={Boolean(hasValidRolesToAccessThirdeye)}>
              <ProtectedExternalLinkButton
                label="Thirdeye"
                tooltipText="Thirdeye"
                link={thirdeyeRedirectUrl}
                shouldOpenLinkHandler={handleShouldOpenThirdeyeLink}
              />
            </ConditionalRenderer>
          </ConditionalRenderer>

          <ConditionalRenderer check={appConfig.app.is_membership_console_link_enabled}>
            <ConditionalRenderer check={Boolean(hasValidRolesToAccessMembershipPortal)}>
              <ProtectedExternalLink
                label="Membership portal"
                link={membershipPortalRedirectUrl}
                tooltipText="Membership portal"
                resource="NavItems.MembershipPortalLink"
              />
            </ConditionalRenderer>
          </ConditionalRenderer>
        </Menu>
      </div>

      <Modal
        variant="curvedEdge"
        isOpen={isModalOpen}
        toggleModal={(val: boolean) => {
          setIsModalOpen(false);
        }}
        fullWidth
        size="md"
      >
        <div>
          <section className="text-center">
            <div className="mb-4 flex justify-center">
              <SandboxSvg />
            </div>
            <PageTitle
              title="Thirdeye configuration is not complete"
              semiBold={true}
              customClass="flex justify-center"
            />
            <Description
              type="text"
              text="Please ensure thirdye configuration is complete and wiam clients are enabled"
            />
          </section>
        </div>
      </Modal>
    </>
  );
};
export default NavItems;
